import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isTokenValid } from '../utils/tokenService';
import { theme } from '../utils/theme';
import LanguageSelector from './LanguageSelector';

const Navbar = () => {
  const { t } = useTranslation('navbar');
  const [user, setUser] = useState(null);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const menuRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const location = useLocation();

  // Handler to update user state based on local storage and token validity
  const handleAuthChange = () => {
    if (isTokenValid()) {
      const storedUser = JSON.parse(localStorage.getItem('userData') || 'null');
      setUser(storedUser && storedUser.name ? storedUser : null);
    } else {
      setUser(null);
    }
  };

  const scrollToSection = (sectionId) => {
    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }
    
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigationLinks = [
    { id: 'home', label: t('navigation.home') },
    { id: 'features', label: t('navigation.features') },
    { id: 'how-we-work', label: t('navigation.howItWorks') },
    { id: 'gallery', label: t('navigation.gallery') },
    { id: 'testimonials', label: t('navigation.testimonials') },
    { id: 'pricing', label: t('navigation.pricing') },
    { id: 'faq', label: t('navigation.faq') }
  ];

  // Updated handleLogout to navigate to index page if not already there
  const handleLogout = () => {
    // Remove tokens and user data from local storage
    localStorage.removeItem('userData');
    localStorage.removeItem('token');
    // Dispatch authChange event so other parts of UI update
    window.dispatchEvent(new Event('authChange'));
    // Navigate to the index page if not already there
    if (window.location.pathname !== '/') {
      navigate('/');
    }
  };

  // Listen for custom 'authChange' event to update navbar state
  useEffect(() => {
    handleAuthChange(); // Set initial state
    window.addEventListener('authChange', handleAuthChange);
    return () => window.removeEventListener('authChange', handleAuthChange);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMobileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCreateNewMemory = () => {
    // Clear any existing bucket ID and uploads from session storage
    sessionStorage.removeItem('existingBucketId');
    sessionStorage.removeItem('uploads');
    navigate('/process');
  };

  return (
    <nav className="bg-background-primary/75 fixed top-0 left-0 right-0 shadow backdrop-blur-sm z-50">
      <div className="w-full max-w-[97%] mx-auto py-3 px-2 flex justify-between items-center">
        {/* Left Side - Main Navigation */}
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center flex-shrink-0">
            <img src="/logo.png" alt="Memory Motion" className="h-12 w-[180px] object-contain" />
          </Link>
          
          {user && (
            <>
              <Link 
                to="/dashboard" 
                className={`${theme.nav.link} hidden lg:block relative after:absolute after:bottom-0 after:left-0 after:h-[3px] after:w-0 after:bg-primary hover:after:w-full after:transition-all after:duration-500 after:ease-in-out ${pathname === '/dashboard' ? 'after:w-full' : ''}`}
              >
                {t('userNav.memoryGallery')}
              </Link>
              <button 
                onClick={handleCreateNewMemory}
                className={`${theme.nav.link} hidden lg:block relative after:absolute after:bottom-0 after:left-0 after:h-[3px] after:w-0 after:bg-primary hover:after:w-full after:transition-all after:duration-500 after:ease-in-out ${pathname === '/process' ? 'after:w-full' : ''}`}
              >
                {t('userNav.createNewMemory')}
              </button>
            </>
          )}
          {!user && (
            <>
              {navigationLinks.map(link => (
                <button 
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className={`${theme.nav.link} hidden 2xl:block relative after:absolute after:bottom-0 after:left-0 after:h-[3px] after:w-0 after:bg-primary hover:after:w-full after:transition-all after:duration-500 after:ease-in-out`}
                >
                  {link.label}
                </button>
              ))}
            </>
          )}
        </div>

        {/* Right Side - User Elements */}
        <div className={`${user ? 'hidden lg:flex' : 'hidden 2xl:flex'} items-center gap-4`}>
          {!user ? (
            <>
              <LanguageSelector />
              <Link to="/login" className={theme.nav.button.base + ' ' + theme.nav.button.secondary}>
                <span className="mr-2">🔗</span> {t('auth.signIn')}
              </Link>
              <Link to="/register" className={theme.nav.button.base + ' ' + theme.nav.button.primary}>
                {t('auth.getStarted')}
              </Link>
            </>
          ) : (
            <div className="relative" ref={profileDropdownRef}>
              <button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className={theme.nav.button.base + ' ' + theme.nav.button.secondary + ' flex items-center gap-2'}
              >
                <span>{user.name}</span>
                <svg className={`w-4 h-4 transform transition-transform ${showProfileDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Profile Dropdown */}
              {showProfileDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                  <div className="px-4 py-2 text-sm text-gray-500 border-b border-gray-100">
                    {t('profile.welcome')}, {user.name}
                  </div>
                  {user?.role === 'Admin' && (
                    <Link to="/admin" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      {t('profile.adminDashboard')}
                    </Link>
                  )}
                  <Link to="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    {t('profile.myProfile')}
                  </Link>
                  <div className="px-4 py-2">
                    <LanguageSelector />
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                  >
                    {t('profile.logout')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile Menu Button - Show based on user status */}
        <div className={`${user ? 'lg:hidden' : '2xl:hidden'} flex items-center`} ref={menuRef}>
          <button
            onClick={() => setMobileMenu(!mobileMenu)}
            className={theme.nav.button.base + ' hover:bg-gray-100'}
            aria-label="Toggle menu"
          >
            <div className="w-6 h-5 flex flex-col justify-between relative">
              <span className={`w-full h-0.5 bg-gray-600 rounded-full transform transition-transform duration-300 ${mobileMenu ? 'rotate-45 translate-y-2' : ''}`}></span>
              <span className={`w-full h-0.5 bg-gray-600 rounded-full transition-opacity duration-300 ${mobileMenu ? 'opacity-0' : ''}`}></span>
              <span className={`w-full h-0.5 bg-gray-600 rounded-full transform transition-transform duration-300 ${mobileMenu ? '-rotate-45 -translate-y-2' : ''}`}></span>
            </div>
          </button>

          {/* Mobile Menu - Show based on user status */}
          {mobileMenu && (
            <div className="absolute top-full right-0 w-[300px] bg-white/90 shadow-lg">
              <div className="w-full px-4 py-4 space-y-2">
                {user ? (
                  <>
                    <div className="px-4 py-2 text-sm text-gray-500 border-b border-gray-100">
                      {t('profile.welcome')}, {user.name}
                    </div>
                    <Link to="/dashboard" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      {t('userNav.memoryGallery')}
                    </Link>
                    <button
                      onClick={handleCreateNewMemory}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {t('userNav.createNewMemory')}
                    </button>
                    {user?.role === 'Admin' && (
                      <Link
                        to="/admin"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {t('profile.adminDashboard')}
                      </Link>
                    )}
                    <Link to="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      {t('profile.myProfile')}
                    </Link>
                    <div className="px-4 py-2">
                      <LanguageSelector />
                    </div>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                    >
                      {t('profile.logout')}
                    </button>
                  </>
                ) : (
                  <>
                    {/* Navigation Links for Mobile */}
                    {navigationLinks.map(link => (
                      <button 
                        key={link.id}
                        onClick={() => {
                          scrollToSection(link.id);
                          setMobileMenu(false); // Close menu after clicking
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {link.label}
                      </button>
                    ))}
                    <div className="px-4 py-2">
                      <LanguageSelector />
                    </div>
                    <div className="border-t border-gray-100 mt-4 pt-4">
                      <Link to="/login" className="block w-full text-center py-2 px-4 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
                        {t('auth.signIn')}
                      </Link>
                      <Link to="/register" className="block w-full text-center py-2 px-4 mt-2 rounded-lg bg-primary text-white hover:bg-primary-dark">
                        {t('auth.getStarted')}
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;